import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import API from "../../../util/api";

const Container = styled.div`
  padding: 20px;
`;
const HeaderWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;
const HeaderButton = styled.a`
  padding: 6px 12px;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border-radius: 4px;
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;
const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  align-items: center;
  label {
    font-size: 14px;
    margin-right: 8px;
    color: ${({ colors }) => colors.labelText};
  }
  select,
  input {
    padding: 5px 10px;
    font-size: 14px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }
  input {
    width: 400px;
  }
`;
const TableWrapper = styled.div`
  overflow-x: auto;
`;
const Table = styled.table`
  ${adminTable};
  font-size: 14px;
  thead th {
    ${adminTableHeaderCell};
    background: #fff;
    position: relative;
    .sort-icons {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      font-size: 10px;
      span {
        cursor: pointer;
        line-height: 10px;
        color: #9CA3AF;
        display: block;
      }
      .active {
        color: #A855F7;
      }
    }
  }
  tbody td {
    ${adminTableDataCell};
    padding: 10px;
  }
`;
const InfoText = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};
`;
const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  button {
    padding: 5px 10px;
    margin-left: 5px;
    border: 1px solid ${({ colors }) => colors.border};
    background: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;
    &:disabled {
      background: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }
    &:hover:not(:disabled) {
      background: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }
`;
const AdvancedForm = styled.form`
  max-width: 500px;
  margin: 0 auto;
  .input {
    margin-bottom: 1rem;
    label {
      display: block;
      margin-bottom: 0.25rem;
      color: ${({ colors }) => colors.labelText};
      font-size: 14px;
    }
    input {
      width: 100%;
      padding: 6px;
      border: 1px solid ${({ colors }) => colors.border};
      border-radius: 4px;
      font-size: 13px;
    }
  }
  .submit {
    margin-top: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    input {
      padding: 8px 16px;
      background: ${({ colors }) => colors.buttonBackground};
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
      &:hover {
        background: ${({ colors }) => colors.buttonHoverBackground};
      }
    }
  }
`;
const CancelLink = styled.a`
  display: inline-block;
  color: ${({ colors }) => colors.buttonBackground};
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const DetailLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
`;

const ActionsPanel = styled.div`
  ${actionContainerStyles};
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ colors }) => colors.border};
  box-shadow: 2px 5px 5px rgba(0, 0, 0, 0.05);

  @media (min-width: 768px) {
    flex: 0 0 260px;
  }
`;

const ActionsHeader = styled.div`
  ${actionHeaderStyles};
  border-bottom: 1px solid ${({ colors }) => colors.border};
`;

const ActionsBody = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionButton = styled.button`
  ${actionLinkStyles};
  padding-left: 16px;
  background: none;
  border: none;
  text-align: left;
  color: #2C4960;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ colors }) => colors.buttonBackground};
  }

  &:disabled {
    color: ${({ colors }) => colors.labelText};
    cursor: default;
    opacity: 0.65;
  }
`;

const DetailPanel = styled.div`
  flex: 1;
  min-width: 0;
  background: #ffffff;
  border: 1px solid ${({ colors }) => colors.border};
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  margin: 0 0 12px;
  color: ${({ colors }) => colors.labelText};
`;

const DetailSection = styled.div`
  margin-bottom: 24px;
`;

const DetailRowsWrapper = styled.div`
  border: 1px solid ${({ colors }) => colors.border};
  border-radius: 6px;
  overflow: hidden;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 220px) 1fr;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ $alt }) => ($alt ? '#f7f7f7' : '#ffffff')};
  border-bottom: 1px solid ${({ colors }) => colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: ${({ colors }) => colors.labelText};
`;

const DetailValue = styled.span`
  color: ${({ colors }) => colors.labelText};
  white-space: pre-wrap;
`;

const DetailTable = styled.table`
  ${adminTable};
  font-size: 14px;
`;

const DetailTableHeadCell = styled.th`
  ${adminTableHeaderCell};
  background: ${({ colors }) => colors.cardBackground};
  color: ${({ colors }) => colors.labelText};
  border-bottom: 1px solid ${({ colors }) => colors.border};
`;

const DetailTableCell = styled.td`
  ${adminTableDataCell};
  padding: 12px;
  border-bottom: 1px solid ${({ colors }) => colors.border};
  vertical-align: top;
  background: ${({ $alt }) => ($alt ? '#f7f7f7' : '#ffffff')};
  white-space: pre-line;
`;

const DetailMessage = styled.div`
  font-size: 14px;
  color: ${({ colors }) => colors.labelText};
`;

const DetailLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${({ colors }) => colors.buttonBackground};
  cursor: pointer;
  text-decoration: underline;
  font-size: 14px;

  &:hover {
    color: ${({ colors }) => colors.darkBlue};
  }
`;

export default function OrganizationSearchContent({ colors }) {
  // view: "list" | "advanced"
  const [view, setView] = useState("list");

  // table params
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState(null);

  // simple search
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // advanced fields
  const [orgName, setOrgName] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");

  // has an advanced query been applied?
  const [advApplied, setAdvApplied] = useState(false);

  // results
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  // fetch helper
  const fetchData = useCallback(async (payload) => {
    setLoading(true);
    try {
      const resp = await API.searchOrganizations(payload);
      setRows(resp.organizations || []);
      setTotal(resp.total || 0);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [search]);

  // 1) simple search effect
  useEffect(() => {
    if (advApplied) return;
    fetchData({
      is_advance_search: 0,
      search: debouncedSearch,
      order_by: orderBy,
      order,
      page,
      items_per_page: itemsPerPage,
    });
  }, [debouncedSearch, orderBy, order, page, itemsPerPage, advApplied, fetchData]);

  // 2) advanced search effect (includes pagination & sort)
  useEffect(() => {
    if (!advApplied) return;
    fetchData({
      is_advance_search: 1,
      search: "",
      order_by: orderBy,
      order,
      page,
      items_per_page: itemsPerPage,
      organization_name: orgName,
      city,
      zip,
      phone,
    });
  }, [
    advApplied,
    orgName,
    city,
    zip,
    phone,
    orderBy,
    order,
    page,
    itemsPerPage,
    fetchData,
  ]);

  // sort toggle
  const handleSort = (key) => {
    const next = orderBy === key && order === "ASC" ? "DESC" : "ASC";
    setOrderBy(key);
    setOrder(next);
    setPage(0);
  };

  // advanced form submit
  const handleAdvancedSubmit = async (e) => {
    e.preventDefault();
    setAdvApplied(true);
    setView("list");
    setPage(0);
  };

  // reset to simple
  const handleReset = () => {
    setAdvApplied(false);
    setOrgName("");
    setCity("");
    setZip("");
    setPhone("");
    setSearch("");
    setDebouncedSearch("");
    setOrderBy(null);
    setOrder(null);
    setPage(0);
    setItemsPerPage(10);
    setSelectedOrg(null);
    setDetailData(null);
    setDetailError("");
    setDetailLoading(false);
  };

  const handleSelectOrganization = async (orgRow) => {
    if (!orgRow) return;
    setSelectedOrg(orgRow);
    setDetailData(null);
    setDetailError("");
    setDetailLoading(true);
    setView("list");

    const orgId = orgRow.id ?? orgRow.org_id ?? orgRow.organization_id;

    try {
      const response = await API.getOrganizationDetail({
        org_id: orgId,
      });
      setDetailData(response || null);
    } catch (err) {
      console.error("Organization detail fetch failed:", err);
      setDetailError(
        err?.message || "Unable to load organization detail. Please try again."
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const handleNewSearch = () => {
    setSelectedOrg(null);
    setDetailData(null);
    setDetailError("");
    setDetailLoading(false);
    setView("list");
  };

  const handleAdvancedFromDetail = () => {
    setSelectedOrg(null);
    setDetailData(null);
    setDetailError("");
    setDetailLoading(false);
    setView("advanced");
  };

  const detailFieldConfig = useMemo(
    () => [
      { label: "Name", keys: ["name", "org_name", "organization_name"] },
      { label: "Type", keys: ["type", "organization_type", "org_type"] },
      { label: "NAC School ID", keys: ["nac_school_id", "nac_id"] },
      {
        label: "NCES School ID",
        keys: ["nces_school_id", "nces_schoolid", "nces_school_id"],
      },
      {
        label: "NCES District ID",
        keys: ["nces_district_id", "nces_districtid"],
      },
      { label: "Private School ID", keys: ["private_school_id"] },
      { label: "Source", keys: ["source"] },
    ],
    []
  );

  const readDetailValue = (keys) => {
    const candidates = Array.isArray(keys) ? keys : [keys];
    const sources = [detailData?.org, detailData, selectedOrg].filter(Boolean);

    for (const key of candidates) {
      for (const source of sources) {
        const value = source[key];
        if (value !== undefined && value !== null && value !== "") {
          return value;
        }
      }
    }
    return "--";
  };

  const formatAddressEntry = (entry) => {
    if (!entry) return null;

    if (typeof entry === "string") {
      return {
        text: entry,
        source:
          entry.source ||
          detailData?.source ||
          selectedOrg?.source ||
          "--",
      };
    }

    const line1 =
      entry.address ||
      entry.address1 ||
      entry.address_line1 ||
      entry.street ||
      entry.line1 ||
      entry.line_1;
    const line2 =
      entry.address2 ||
      entry.address_line2 ||
      entry.street2 ||
      entry.line2 ||
      entry.line_2;
    const city = entry.city || entry.city_name;
    const state = entry.state || entry.state_code;
    const zip = entry.zip || entry.zip_code || entry.postal_code;

    const lines = [];
    if (line1) lines.push(line1);
    if (line2) lines.push(line2);

    const cityState = [city, state].filter(Boolean).join(", ");
    const cityStateZip = [cityState, zip].filter(Boolean).join(" ");
    if (cityStateZip) lines.push(cityStateZip.trim());

    if (!lines.length) return null;

    return {
      text: lines.join("\n"),
      source: entry.source || detailData?.source || selectedOrg?.source || "--",
    };
  };

  const resolvedAddresses = useMemo(() => {
    const entries = [];
    const seen = new Set();

    const register = (candidate) => {
      const formatted = formatAddressEntry(candidate);
      if (!formatted) return;
      const key = `${formatted.text}__${formatted.source}`;
      if (seen.has(key)) return;
      seen.add(key);
      entries.push(formatted);
    };

    if (detailData?.org) {
      register({
        street: detailData.org.street,
        address2: detailData.org.address2,
        city: detailData.org.city,
        state: detailData.org.state,
        zip: detailData.org.zip,
        source: detailData.org.source,
      });
    }

    if (detailData) {
      if (Array.isArray(detailData.addresses)) {
        detailData.addresses.forEach(register);
      }
      if (Array.isArray(detailData.address)) {
        detailData.address.forEach(register);
      }
      register({
        address: detailData.address,
        address2: detailData.address2,
        street: detailData.street,
        city: detailData.city,
        state: detailData.state,
        zip: detailData.zip,
        source: detailData.source,
      });
    }

    if (selectedOrg) {
      register({
        street: selectedOrg.street,
        address2: selectedOrg.street2,
        city: selectedOrg.city,
        state: selectedOrg.state,
        zip: selectedOrg.zip,
        source: selectedOrg.source,
      });
    }

    return entries;
  }, [detailData, selectedOrg]);

  const formatPhoneEntry = (entry) => {
    if (!entry) return null;
    if (typeof entry === "string") {
      return {
        number: entry,
        source:
          detailData?.source || selectedOrg?.source || entry.source || "--",
      };
    }

    const number =
      entry.phone || entry.phone_number || entry.number || entry.value;
    if (!number) return null;

    const extension = entry.extension || entry.ext || entry.phone_extension;
    const displayNumber = extension ? `${number} x${extension}` : number;

    return {
      number: displayNumber,
      source: entry.source || detailData?.source || selectedOrg?.source || "--",
    };
  };

  const resolvedPhones = useMemo(() => {
    const entries = [];
    const seen = new Set();

    const register = (candidate) => {
      const formatted = formatPhoneEntry(candidate);
      if (!formatted) return;
      const key = `${formatted.number}__${formatted.source}`;
      if (seen.has(key)) return;
      seen.add(key);
      entries.push(formatted);
    };

    if (detailData?.org?.phone) {
      register({
        phone: detailData.org.phone,
        source: detailData.org.source,
      });
    }

    if (detailData) {
      if (Array.isArray(detailData.phones)) {
        detailData.phones.forEach(register);
      }
      if (Array.isArray(detailData.phone_numbers)) {
        detailData.phone_numbers.forEach(register);
      }
      if (detailData.phone) register(detailData.phone);
    }

    if (selectedOrg?.phone) {
      register({ phone: selectedOrg.phone, source: selectedOrg.source });
    }

    return entries;
  }, [detailData, selectedOrg]);

  const toDisplayValue = (value) => {
    if (value === undefined || value === null || value === "") {
      return "--";
    }
    if (Array.isArray(value)) {
      const joined = value.filter(Boolean).join(", ");
      return joined || "--";
    }
    return String(value);
  };

  if (selectedOrg) {
    const detailRows = detailFieldConfig.map((item, index) => ({
      label: item.label,
      value: toDisplayValue(readDetailValue(item.keys)),
      alt: index % 2 === 1,
    }));

    return (
      <Container>
        <DetailLayout>
          <ResponsiveActions
            colors={colors}
            renderActions={() => (
              <ActionsPanel colors={colors}>
                <ActionsHeader colors={colors}>Actions</ActionsHeader>
                <ActionsBody>
                  <ActionList>
                    <li>
                      <ActionButton colors={colors} onClick={handleNewSearch}>
                        New Search
                      </ActionButton>
                    </li>
                    <li>
                      <ActionButton
                        colors={colors}
                        onClick={handleAdvancedFromDetail}
                      >
                        Advanced Search
                      </ActionButton>
                    </li>
                  </ActionList>
                </ActionsBody>
              </ActionsPanel>
            )}
          />

          <DetailPanel colors={colors}>
            <DetailSection>
              <SectionTitle colors={colors}>Organization Detail</SectionTitle>
              {detailLoading ? (
                <DetailMessage colors={colors}>
                  Loading organization detail…
                </DetailMessage>
              ) : detailError ? (
                <DetailMessage colors={colors}>{detailError}</DetailMessage>
              ) : (
                <DetailRowsWrapper colors={colors}>
                  {detailRows.map((row) => (
                    <DetailRow
                      key={row.label}
                      colors={colors}
                      $alt={row.alt}
                    >
                      <DetailLabel colors={colors}>{row.label}</DetailLabel>
                      <DetailValue colors={colors}>{row.value}</DetailValue>
                    </DetailRow>
                  ))}
                </DetailRowsWrapper>
              )}
            </DetailSection>

            <DetailSection>
              <SectionTitle colors={colors}>Address</SectionTitle>
              {detailLoading ? (
                <DetailMessage colors={colors}>Loading…</DetailMessage>
              ) : resolvedAddresses.length ? (
                <DetailTable colors={colors}>
                  <thead>
                    <tr>
                      <DetailTableHeadCell colors={colors}>
                        Address
                      </DetailTableHeadCell>
                      <DetailTableHeadCell colors={colors}>
                        Source
                      </DetailTableHeadCell>
                    </tr>
                  </thead>
                  <tbody>
                    {resolvedAddresses.map((item, index) => (
                      <tr key={`${item.text}-${index}`}>
                        <DetailTableCell
                          colors={colors}
                          $alt={index % 2 === 1}
                        >
                          {item.text}
                        </DetailTableCell>
                        <DetailTableCell
                          colors={colors}
                          $alt={index % 2 === 1}
                        >
                          {toDisplayValue(item.source)}
                        </DetailTableCell>
                      </tr>
                    ))}
                  </tbody>
                </DetailTable>
              ) : (
                <DetailMessage colors={colors}>
                  No address on file.
                </DetailMessage>
              )}
            </DetailSection>

            <DetailSection>
              <SectionTitle colors={colors}>Phone</SectionTitle>
              {detailLoading ? (
                <DetailMessage colors={colors}>Loading…</DetailMessage>
              ) : resolvedPhones.length ? (
                <DetailTable colors={colors}>
                  <thead>
                    <tr>
                      <DetailTableHeadCell colors={colors}>
                        Phone
                      </DetailTableHeadCell>
                      <DetailTableHeadCell colors={colors}>
                        Source
                      </DetailTableHeadCell>
                    </tr>
                  </thead>
                  <tbody>
                    {resolvedPhones.map((item, index) => (
                      <tr key={`${item.number}-${index}`}>
                        <DetailTableCell
                          colors={colors}
                          $alt={index % 2 === 1}
                        >
                          {toDisplayValue(item.number)}
                        </DetailTableCell>
                        <DetailTableCell
                          colors={colors}
                          $alt={index % 2 === 1}
                        >
                          {toDisplayValue(item.source)}
                        </DetailTableCell>
                      </tr>
                    ))}
                  </tbody>
                </DetailTable>
              ) : (
                <DetailMessage colors={colors}>
                  No phone records found.
                </DetailMessage>
              )}
            </DetailSection>
          </DetailPanel>
        </DetailLayout>
      </Container>
    );
  }

  // render advanced form
  if (view === "advanced") {
    return (
      <Container>
        <h1 style={{ color: colors.labelText }}>
          Advanced Organization Search
        </h1>
        <AdvancedForm colors={colors} onSubmit={handleAdvancedSubmit}>
          <div className="input">
            <label>Organization Name</label>
            <input
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
            />
          </div>
          <div className="input">
            <label>City</label>
            <input value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div className="input">
            <label>Zip</label>
            <input value={zip} onChange={(e) => setZip(e.target.value)} />
          </div>
          <div className="input">
            <label>Phone</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="submit">
            <input type="submit" value="Search" />
            <CancelLink colors={colors} onClick={() => setView("list")}>
              Cancel
            </CancelLink>
          </div>
        </AdvancedForm>
      </Container>
    );
  }

  // list view
  const start = page * itemsPerPage + 1;
  const end =
    itemsPerPage === -1 ? total : Math.min((page + 1) * itemsPerPage, total);

  return (
    <Container>
      <HeaderWrapper>
        {advApplied ? (
          <HeaderButton colors={colors} onClick={handleReset}>
            Reset Filters
          </HeaderButton>
        ) : (
          <HeaderButton
            colors={colors}
            onClick={() => {
              setSelectedOrg(null);
              setDetailData(null);
              setDetailError("");
              setDetailLoading(false);
              setView("advanced");
            }}
          >
            Advanced Search
          </HeaderButton>
        )}
      </HeaderWrapper>

      <ControlsContainer colors={colors}>
        <div>
          <label>Show</label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setPage(0);
            }}
          >
            {[10, 25, 50, 100, -1].map((n) => (
              <option key={n} value={n}>
                {n === -1 ? "All" : n}
              </option>
            ))}
          </select>
          <label style={{ marginLeft: "5px" }}>entries</label>
        </div>
        <div>
          <label>Search:</label>
          <input
            type="text"
            placeholder="Type to filter..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            disabled={advApplied}
          />
        </div>
      </ControlsContainer>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          <TableWrapper>
            <Table colors={colors}>
              <thead>
                <tr>
                  {[
                    ["org_name", "Org Name"],
                    ["street", "Street"],
                    ["city", "City"],
                    ["state", "State"],
                    ["zip", "Zip"],
                  ].map(([key, label]) => {
                    const isAscActive = orderBy === key && order === "ASC";
                    const isDescActive = orderBy === key && order === "DESC";
                    const defaultColor = "#9CA3AF";
                    const activeColor = "#A855F7";
                    return (
                      <th key={key}>
                        {label}
                        <div className="sort-icons">
                          <span
                            onClick={() => handleSort(key)}
                            className={isAscActive ? "active" : ""}
                            style={{
                              color: isAscActive ? activeColor : defaultColor,
                              display: isDescActive ? "none" : "block"
                            }}
                          >
                            ▲
                          </span>
                          <span
                            onClick={() => handleSort(key)}
                            className={isDescActive ? "active" : ""}
                            style={{
                              color: isDescActive ? activeColor : defaultColor,
                              display: isAscActive ? "none" : "block"
                            }}
                          >
                            ▼
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows?.map((r) => (
                  <tr key={r.id ?? r.org_id}>
                    <td>
                      <DetailLink
                        type="button"
                        colors={colors}
                        onClick={() => handleSelectOrganization(r)}
                      >
                        {r.org_name}
                      </DetailLink>
                    </td>
                    <td>{r.street}</td>
                    <td>{r.city}</td>
                    <td>{r.state}</td>
                    <td>{r.zip}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          <InfoText colors={colors}>
            Showing {total > 0 ? start : 0} to {end} of {total} entries
          </InfoText>
          <Pagination colors={colors}>
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={page === 0}
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={itemsPerPage === -1 || end === total}
            >
              Next
            </button>
          </Pagination>
        </>
      )}
    </Container>
  );
}
